/**
 * Section Registry
 * Universal section library with all available section types and their configurations.
 * Used by both Builder (for section selection) and Viewer (for rendering).
 */

// =============================================================================
// Types
// =============================================================================

export type SectionCategory =
  | 'hero'
  | 'content'
  | 'features'
  | 'cta'
  | 'social'
  | 'utility'

export type PaddingSize = 'none' | 'small' | 'medium' | 'large'

export interface SectionSettings {
  backgroundColor?: string
  textColor?: string
  paddingTop?: PaddingSize
  paddingBottom?: PaddingSize
  fullWidth?: boolean
  customClasses?: string
}

export type FieldType =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'image'
  | 'color'
  | 'select'
  | 'number'
  | 'boolean'
  | 'array'
  | 'icon'
  | 'slider'
  | 'section_select'
  | 'vehicle_select'

export interface FieldConfig {
  key: string
  label: string
  type: FieldType
  description?: string
  placeholder?: string
  required?: boolean
  options?: Array<{ value: string; label: string }>
  arrayItemSchema?: FieldConfig[]
  defaultValue?: unknown
  // For slider type
  min?: number
  max?: number
  step?: number
  // For conditional visibility (show field only when another field has a specific value)
  conditionalOn?: {
    key: string
    value: string | string[] | boolean
  }
}

export interface SectionTypeDefinition {
  type: string
  name: string
  description: string
  icon: string // Lucide icon name
  category: SectionCategory
  supportsMultiple: boolean
  defaultSettings: SectionSettings
  defaultContent: Record<string, unknown>
  fields: FieldConfig[]
}

// =============================================================================
// Section Registry
// =============================================================================

export const SECTION_REGISTRY: Record<string, SectionTypeDefinition> = {
  // =========================================================================
  // HERO SECTIONS
  // =========================================================================
  hero_banner: {
    type: 'hero_banner',
    name: 'Hero Banner',
    description: 'Large hero banner with tagline and background image',
    icon: 'Image',
    category: 'hero',
    supportsMultiple: false,
    defaultSettings: {
      paddingTop: 'none',
      paddingBottom: 'none',
    },
    defaultContent: {
      tagline_line1: 'Your Personal',
      tagline_line2: 'Chauffeur Service',
      background_image_url: '',
      image_position_mode: 'cover_center',
      overlay_enabled: true,
      overlay_opacity: 30,
      overlay_feathered: false,
    },
    fields: [
      {
        key: 'tagline_line1',
        label: 'Tagline Line 1',
        type: 'text',
        placeholder: 'Your Personal',
      },
      {
        key: 'tagline_line2',
        label: 'Tagline Line 2',
        type: 'text',
        placeholder: 'Chauffeur Service',
      },
      {
        key: 'background_image_url',
        label: 'Background Image',
        type: 'image',
        description: 'Leave empty to use default gradient',
      },
      {
        key: 'image_position_mode',
        label: 'Background Image Style',
        type: 'select',
        description: 'How the background image is displayed and positioned',
        options: [
          { value: 'cover_center', label: 'Cover (Centered)' },
          { value: 'cover_top', label: 'Cover (Top-aligned)' },
          { value: 'cover_bottom', label: 'Cover (Bottom-aligned)' },
          { value: 'parallax', label: 'Parallax Effect' },
          { value: 'tile', label: 'Tile / Repeat Pattern' },
        ],
        defaultValue: 'cover_center',
      },
      {
        key: 'overlay_enabled',
        label: 'Show Dark Overlay',
        type: 'boolean',
        defaultValue: true,
      },
      {
        key: 'overlay_opacity',
        label: 'Overlay Opacity',
        type: 'number',
        description: '0 (transparent) to 100 (fully dark)',
        defaultValue: 30,
      },
      {
        key: 'overlay_feathered',
        label: 'Feathered Edges',
        type: 'boolean',
        description: 'Add gradient darkening on left/right edges',
        defaultValue: false,
      },
    ],
  },

  hero_booking: {
    type: 'hero_booking',
    name: 'Hero with Booking Widget',
    description: 'Hero banner with integrated booking form',
    icon: 'CalendarCheck',
    category: 'hero',
    supportsMultiple: false,
    defaultSettings: {
      paddingTop: 'none',
      paddingBottom: 'none',
    },
    defaultContent: {
      variant: 'classic',
      show_nav: true,
      tagline_line1: 'Your Personal',
      tagline_line2: 'Chauffeur Service',
      background_image_url: '',
      image_position_mode: 'cover_center',
      overlay_enabled: true,
      overlay_opacity: 30,
      overlay_feathered: false,
      description:
        'Travel in comfort and style with our professional chauffeur service. Reliable rides for airports, events, and everything in between.',
      contact_link_text: 'Contact us',
      contact_link_url: '/contact',
    },
    fields: [
      // Style variations — each option maps to a component in
      // apps/viewer/src/components/sections/hero-booking/. Author new ones
      // with /section:variation (see the section-authoring skill).
      {
        key: 'variant',
        label: 'Style Variation',
        type: 'select',
        description: 'The layout/style of this section — content fields work in every variation',
        options: [
          { value: 'classic', label: 'Classic — headline left, booking widget right' },
          { value: 'centered_spotlight', label: 'Centered Spotlight — headline and widget centered over the image' },
          { value: 'midnight_showcase', label: 'Midnight Showcase — dark hero, text left, booking card floating over the bottom edge' },
        ],
        defaultValue: 'classic',
      },
      {
        key: 'show_nav',
        label: 'Show Navigation Bar',
        type: 'boolean',
        // This section draws its own nav overlaid on the hero. On the home page
        // that is the ONLY nav (the renderer skips the page layout entirely),
        // but on any other page the page's own header renders too — giving two
        // headers. Turn this off to keep the page header and drop the hero's.
        description:
          "The hero's own navigation, drawn over the image. Turn it off if the page already shows a header.",
        defaultValue: true,
      },
      {
        key: 'tagline_line1',
        label: 'Tagline Line 1',
        type: 'text',
        placeholder: 'Your Personal',
      },
      {
        key: 'tagline_line2',
        label: 'Tagline Line 2',
        type: 'text',
        placeholder: 'Chauffeur Service',
      },
      {
        key: 'background_image_url',
        label: 'Background Image',
        type: 'image',
        description: 'Leave empty to use default gradient',
      },
      {
        key: 'image_position_mode',
        label: 'Background Image Style',
        type: 'select',
        description: 'How the background image is displayed and positioned',
        options: [
          { value: 'cover_center', label: 'Cover (Centered)' },
          { value: 'cover_top', label: 'Cover (Top-aligned)' },
          { value: 'cover_bottom', label: 'Cover (Bottom-aligned)' },
          { value: 'parallax', label: 'Parallax Effect' },
          { value: 'tile', label: 'Tile / Repeat Pattern' },
        ],
        defaultValue: 'cover_center',
      },
      {
        key: 'overlay_enabled',
        label: 'Show Dark Overlay',
        type: 'boolean',
        defaultValue: true,
      },
      {
        key: 'overlay_opacity',
        label: 'Overlay Opacity',
        type: 'number',
        description: '0 (transparent) to 100 (fully dark)',
        defaultValue: 30,
      },
      {
        key: 'overlay_feathered',
        label: 'Feathered Edges',
        type: 'boolean',
        description: 'Add gradient darkening on left/right edges',
        defaultValue: false,
      },
      // Midnight Showcase-only fields
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        description: 'Short paragraph shown under the headline',
        conditionalOn: { key: 'variant', value: 'midnight_showcase' },
      },
      {
        key: 'contact_link_text',
        label: 'Contact Link Text',
        type: 'text',
        placeholder: 'Contact us',
        description: 'Leave empty to hide the contact link',
        conditionalOn: { key: 'variant', value: 'midnight_showcase' },
      },
      {
        key: 'contact_link_url',
        label: 'Contact Link URL',
        type: 'text',
        placeholder: '/contact',
        conditionalOn: { key: 'variant', value: 'midnight_showcase' },
      },
    ],
  },

  hero_simple: {
    type: 'hero_simple',
    name: 'Simple Page Header',
    description: 'Centered heading with optional description',
    icon: 'Type',
    category: 'hero',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'medium',
    },
    defaultContent: {
      label: '',
      heading: 'Page Title',
      description: '',
    },
    fields: [
      {
        key: 'label',
        label: 'Label',
        type: 'text',
        placeholder: 'ABOUT US',
        description: 'Small text above heading',
      },
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Page Title',
        required: true,
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Optional description text',
      },
    ],
  },

  // =========================================================================
  // CONTENT SECTIONS
  // =========================================================================
  fleet_carousel: {
    type: 'fleet_carousel',
    name: 'Fleet Carousel',
    description: 'Scrolling showcase of your vehicle fleet',
    icon: 'Car',
    category: 'content',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      label: 'OUR FLEET',
      heading: 'Comfort, privacy and luxury.',
      description:
        'Experience the ultimate private chauffeur service. Encounter every destination in our top of the line vehicles.',
      card_size: 'medium',
      card_size_custom: 300,
      carousel_centered: false,
      carousel_feathered_edges: false,
      disable_vehicle_links: false,
      max_vehicles: 5,
      use_specific_vehicles: false,
      selected_vehicle_ids: [],
    },
    fields: [
      { key: 'label', label: 'Label', type: 'text', placeholder: 'OUR FLEET' },
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Comfort, privacy and luxury.',
      },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'use_specific_vehicles',
        label: 'Select Specific Vehicles',
        type: 'boolean',
        description:
          'Choose exactly which vehicles to display instead of showing the first several.',
        defaultValue: false,
      },
      {
        key: 'selected_vehicle_ids',
        label: 'Vehicles to Display',
        type: 'vehicle_select',
        description:
          'Only the checked vehicles will appear, in your fleet display order.',
        defaultValue: [],
        conditionalOn: { key: 'use_specific_vehicles', value: true },
      },
      {
        key: 'max_vehicles',
        label: 'Number of Vehicles to Display',
        type: 'number',
        description: 'How many vehicles to show (in fleet display order).',
        defaultValue: 5,
        conditionalOn: { key: 'use_specific_vehicles', value: false },
      },
      {
        key: 'card_size',
        label: 'Card Size',
        type: 'select',
        options: [
          { value: 'small', label: 'Small (200px)' },
          { value: 'medium', label: 'Medium (256px)' },
          { value: 'large', label: 'Large (320px)' },
          { value: 'custom', label: 'Custom' },
        ],
        defaultValue: 'medium',
      },
      {
        key: 'card_size_custom',
        label: 'Custom Width (pixels)',
        type: 'number',
        description: 'Custom width between 150 and 750 pixels',
        defaultValue: 300,
        conditionalOn: { key: 'card_size', value: 'custom' },
      },
      {
        key: 'carousel_centered',
        label: 'Center Carousel',
        type: 'boolean',
        description: 'Center vehicles so edges are cut equally on both sides',
        defaultValue: false,
      },
      {
        key: 'carousel_feathered_edges',
        label: 'Feathered Edges',
        type: 'boolean',
        description: 'Add gradient fade on left and right edges',
        defaultValue: false,
      },
      {
        key: 'disable_vehicle_links',
        label: 'Disable Vehicle Links',
        type: 'boolean',
        description: 'Prevent clicking vehicle cards to view details',
        defaultValue: false,
      },
    ],
  },

  fleet_grid: {
    type: 'fleet_grid',
    name: 'Fleet Grid',
    description: 'Full vehicle listing grid (for fleet page)',
    icon: 'LayoutGrid',
    category: 'content',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#f9fafb',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {},
    fields: [],
  },

  intercity_routes: {
    type: 'intercity_routes',
    name: 'Popular Routes',
    description: 'Grid of intercity route cards',
    icon: 'MapPin',
    category: 'content',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#f9fafb',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Intercity Rides',
      description:
        'Skip the airport lines, drop the station crowds. Ride your personal intercity car.',
      routes: [
        {
          from: 'New York City',
          to: 'Atlantic City',
          miles: '125 miles',
          time: '2h 00m',
          image_url:
            'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&q=80',
        },
        {
          from: 'New York City',
          to: 'Hamptons',
          miles: '95 miles',
          time: '2h 00m',
          image_url:
            'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80',
        },
        {
          from: 'New York City',
          to: 'Philadelphia',
          miles: '95 miles',
          time: '1h 45m',
          image_url:
            'https://images.unsplash.com/photo-1601042879364-f3947d3f9c16?w=400&q=80',
        },
      ],
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Intercity Rides',
      },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'routes',
        label: 'Routes',
        type: 'array',
        arrayItemSchema: [
          { key: 'from', label: 'From', type: 'text' },
          { key: 'to', label: 'To', type: 'text' },
          { key: 'miles', label: 'Miles', type: 'text' },
          { key: 'time', label: 'Time', type: 'text' },
          { key: 'image_url', label: 'Image', type: 'image' },
        ],
      },
    ],
  },

  story_section: {
    type: 'story_section',
    name: 'Story / About Text',
    description: 'Rich text content block for telling your story',
    icon: 'FileText',
    category: 'content',
    supportsMultiple: true,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Our Story',
      content:
        'We are dedicated to providing exceptional luxury transportation services. Our commitment to excellence, punctuality, and customer satisfaction sets us apart in the industry.',
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Our Story',
      },
      {
        key: 'content',
        label: 'Content',
        type: 'richtext',
        description: 'Rich text content with formatting',
      },
    ],
  },

  contact_form: {
    type: 'contact_form',
    name: 'Contact Form',
    description: 'Contact form with service type selection',
    icon: 'Mail',
    category: 'content',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Request a Quote',
      description: "Fill out the form below and we'll get back to you shortly.",
      service_types: [
        { value: 'airport', label: 'Airport Transfer' },
        { value: 'hourly', label: 'Hourly Charter' },
        { value: 'event', label: 'Special Event' },
        { value: 'corporate', label: 'Corporate Transportation' },
        { value: 'other', label: 'Other' },
      ],
      submit_button_text: 'Send Message',
      success_message:
        "Thank you! Your message has been sent. We'll be in touch soon.",
      error_message: 'Something went wrong. Please try again.',
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Request a Quote',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: "Fill out the form below and we'll get back to you shortly.",
      },
      {
        key: 'service_types',
        label: 'Service Types',
        type: 'array',
        arrayItemSchema: [
          { key: 'value', label: 'Value', type: 'text' },
          { key: 'label', label: 'Label', type: 'text' },
        ],
      },
      {
        key: 'submit_button_text',
        label: 'Submit Button Text',
        type: 'text',
        defaultValue: 'Send Message',
      },
      {
        key: 'success_message',
        label: 'Success Message',
        type: 'textarea',
        defaultValue: "Thank you! Your message has been sent. We'll be in touch soon.",
      },
      {
        key: 'error_message',
        label: 'Error Message',
        type: 'textarea',
        defaultValue: 'Something went wrong. Please try again.',
      },
    ],
  },

  faq_accordion: {
    type: 'faq_accordion',
    name: 'FAQ Accordion',
    description: 'Expandable FAQ items (pulls from FAQ database)',
    icon: 'HelpCircle',
    category: 'content',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Frequently Asked Questions',
      description: 'Find answers to common questions about our services',
      empty_heading: 'FAQ Coming Soon',
      empty_description:
        'Our FAQ section is being updated. Please contact us if you have any questions.',
      empty_button_text: 'Contact Us',
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Frequently Asked Questions',
      },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'empty_heading',
        label: 'Empty State Heading',
        type: 'text',
        description: 'Shown when no FAQs exist',
      },
      {
        key: 'empty_description',
        label: 'Empty State Description',
        type: 'textarea',
      },
      {
        key: 'empty_button_text',
        label: 'Empty State Button',
        type: 'text',
      },
    ],
  },

  // =========================================================================
  // FEATURES SECTIONS
  // =========================================================================
  features_grid: {
    type: 'features_grid',
    name: 'Features Grid',
    description: 'Grid of feature cards with icons',
    icon: 'Grid3x3',
    category: 'features',
    supportsMultiple: true,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Why Choose Us',
      description: 'We go above and beyond to ensure every ride is exceptional',
      features: [
        {
          icon: 'Shield',
          title: 'Guaranteed Pricing',
          description:
            'Rates from pick-up to drop-off are guaranteed at the time of booking.',
        },
        {
          icon: 'Users',
          title: 'Professional Drivers',
          description:
            "Sit back and relax. You're in the best hands with our professional chauffeurs.",
        },
        {
          icon: 'Car',
          title: 'Best-in-class Cars',
          description:
            'Our extensive fleet ranges from Limousines, Sedans, SUVs and Sprinter Vans.',
        },
      ],
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Why Choose Us',
      },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'features',
        label: 'Features',
        type: 'array',
        arrayItemSchema: [
          { key: 'icon', label: 'Icon', type: 'icon' },
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
        ],
      },
    ],
  },

  services_grid: {
    type: 'services_grid',
    name: 'Services Grid',
    description: 'Grid of service cards with images',
    icon: 'Briefcase',
    category: 'features',
    supportsMultiple: true,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Our Services',
      description:
        'Best-in-class luxury vehicles for every professional chauffeured ride you may need.',
      services: [
        {
          title: 'Airport Car Service',
          description:
            'Skip the rental lines when you plan your custom ride to the airport.',
          image_url: '/images/services/airport.jpg',
        },
        {
          title: 'Luxury Black Car Service',
          description:
            'Enjoy exceptional, luxury transfer service with our professional chauffeurs.',
          image_url: '/images/services/black-car.jpg',
        },
        {
          title: 'Private Van Service',
          description:
            'Enjoy total spacious chauffeur service for your whole group.',
          image_url: '/images/services/van.jpg',
        },
      ],
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Our Services',
      },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'services',
        label: 'Services',
        type: 'array',
        arrayItemSchema: [
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'image_url', label: 'Image', type: 'image' },
          {
            key: 'link_url',
            label: 'Learn More Link',
            type: 'text',
            placeholder: 'https://example.com or /fleet',
            description: 'Optional link for Learn More button. Leave empty to hide the button.',
          },
        ],
      },
    ],
  },

  stats_section: {
    type: 'stats_section',
    name: 'Statistics',
    description: 'Highlight key numbers and achievements',
    icon: 'BarChart3',
    category: 'features',
    supportsMultiple: true,
    defaultSettings: {
      backgroundColor: '#0f172a',
      textColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      heading: 'Our Track Record',
      content:
        'To deliver an unparalleled transportation experience that combines luxury, reliability, and personalized service.',
      stats: [
        { value: '100%', label: 'Professional Drivers' },
        { value: '24/7', label: 'Availability' },
        { value: '5 Star', label: 'Service' },
      ],
    },
    fields: [
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Our Track Record',
      },
      {
        key: 'content',
        label: 'Content',
        type: 'textarea',
        description: 'Optional description text',
      },
      {
        key: 'stats',
        label: 'Statistics',
        type: 'array',
        arrayItemSchema: [
          { key: 'value', label: 'Value', type: 'text', placeholder: '100%' },
          {
            key: 'label',
            label: 'Label',
            type: 'text',
            placeholder: 'Professional Drivers',
          },
        ],
      },
    ],
  },

  // =========================================================================
  // CTA SECTIONS
  // =========================================================================
  cta_banner: {
    type: 'cta_banner',
    name: 'CTA Banner',
    description: 'Call-to-action banner with buttons',
    icon: 'Megaphone',
    category: 'cta',
    supportsMultiple: true,
    defaultSettings: {
      backgroundColor: '#0f172a',
      textColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      line1: "It's not just a ride,",
      line2: "it's a one of a kind experience.",
      primary_button_text: 'Book Now',
      primary_button_link_type: 'url',
      primary_button_url: '/book',
      secondary_button_text: 'Contact Us',
      secondary_button_link_type: 'url',
      secondary_button_url: '/contact',
    },
    fields: [
      { key: 'line1', label: 'Line 1', type: 'text', placeholder: "It's not just a ride," },
      { key: 'line2', label: 'Line 2', type: 'text', placeholder: "it's a one of a kind experience." },
      {
        key: 'primary_button_text',
        label: 'Primary Button Text',
        type: 'text',
        placeholder: 'Book Now',
      },
      {
        key: 'primary_button_link_type',
        label: 'Primary Button Action',
        type: 'select',
        options: [
          { value: 'url', label: 'Link/URL' },
          { value: 'section', label: 'Page Section' },
        ],
        defaultValue: 'url',
      },
      {
        key: 'primary_button_url',
        label: 'Primary Button URL',
        type: 'text',
        placeholder: '/book',
        conditionalOn: { key: 'primary_button_link_type', value: 'url' },
      },
      {
        key: 'primary_button_section_id',
        label: 'Scroll to Section',
        type: 'section_select',
        description: 'Select a section on this page to scroll to when clicked',
        conditionalOn: { key: 'primary_button_link_type', value: 'section' },
      },
      {
        key: 'secondary_button_text',
        label: 'Secondary Button Text',
        type: 'text',
        placeholder: 'Contact Us',
        description: 'Leave empty to hide',
      },
      {
        key: 'secondary_button_link_type',
        label: 'Secondary Button Action',
        type: 'select',
        options: [
          { value: 'url', label: 'Link/URL' },
          { value: 'section', label: 'Page Section' },
        ],
        defaultValue: 'url',
      },
      {
        key: 'secondary_button_url',
        label: 'Secondary Button URL',
        type: 'text',
        placeholder: '/contact',
        conditionalOn: { key: 'secondary_button_link_type', value: 'url' },
      },
      {
        key: 'secondary_button_section_id',
        label: 'Scroll to Section',
        type: 'section_select',
        description: 'Select a section on this page to scroll to when clicked',
        conditionalOn: { key: 'secondary_button_link_type', value: 'section' },
      },
    ],
  },

  // =========================================================================
  // SOCIAL SECTIONS
  // =========================================================================
  reviews_carousel: {
    type: 'reviews_carousel',
    name: 'Reviews Carousel',
    description: 'Scrolling customer testimonials (pulls from Reviews database)',
    icon: 'MessageSquareQuote',
    category: 'social',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#f9fafb',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      variant: 'classic',
      heading: 'Rated 5 out of 5 stars',
      subheading: 'by our customers!',
      description:
        'Customers from across the globe have had fantastic experiences.',
    },
    fields: [
      // Style variations — each option maps to a component in
      // apps/viewer/src/components/sections/reviews-carousel/. Author new
      // ones with /section:variation (see the section-authoring skill).
      {
        key: 'variant',
        label: 'Style Variation',
        type: 'select',
        description: 'The layout/style of this section — content fields work in every variation',
        options: [
          { value: 'classic', label: 'Classic — rating text left, scrolling review columns right' },
          { value: 'spotlight', label: 'Spotlight — one large centered quote at a time with arrows and dots' },
        ],
        defaultValue: 'classic',
      },
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'Rated 5 out of 5 stars',
      },
      {
        key: 'subheading',
        label: 'Subheading',
        type: 'text',
        placeholder: 'by our customers!',
      },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
  },

  reviews_horizontal: {
    type: 'reviews_horizontal',
    name: 'Reviews Carousel (Horizontal)',
    description: 'Horizontal scrolling testimonials in a single row',
    icon: 'MessageSquareQuote',
    category: 'social',
    supportsMultiple: false,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'large',
      paddingBottom: 'large',
    },
    defaultContent: {
      label: 'TESTIMONIALS',
      heading: 'What Our Customers Say',
      description: 'See why customers love our service.',
      show_google_logo: false,
      show_tripadvisor_logo: false,
      card_size: 'medium',
      auto_scroll: true,
      auto_scroll_speed: 0.5,
      feathered_edges: true,
      show_navigation: true,
    },
    fields: [
      {
        key: 'label',
        label: 'Label',
        type: 'text',
        placeholder: 'TESTIMONIALS',
        description: 'Small text above heading',
      },
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        placeholder: 'What Our Customers Say',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'See why customers love our service.',
      },
      {
        key: 'show_google_logo',
        label: 'Show Google Logo',
        type: 'boolean',
        description: 'Large Google logo above the description',
        defaultValue: false,
      },
      {
        key: 'show_tripadvisor_logo',
        label: 'Show Tripadvisor Logo',
        type: 'boolean',
        description: 'Large Tripadvisor logo above the description (a dot separates it from the Google logo when both are on)',
        defaultValue: false,
      },
      {
        key: 'card_size',
        label: 'Card Size',
        type: 'select',
        options: [
          { value: 'small', label: 'Small (280px)' },
          { value: 'medium', label: 'Medium (340px)' },
          { value: 'large', label: 'Large (400px)' },
        ],
        defaultValue: 'medium',
      },
      {
        key: 'auto_scroll',
        label: 'Auto Scroll',
        type: 'boolean',
        description: 'Automatically scroll through reviews',
        defaultValue: true,
      },
      {
        key: 'auto_scroll_speed',
        label: 'Auto Scroll Speed',
        type: 'slider',
        description: 'Scroll speed (0.3 = slow, 1 = medium, 3 = fast)',
        min: 0.3,
        max: 3,
        step: 0.1,
        defaultValue: 0.5,
        conditionalOn: { key: 'auto_scroll', value: true },
      },
      {
        key: 'feathered_edges',
        label: 'Feathered Edges',
        type: 'boolean',
        description: 'Add gradient fade on left and right edges',
        defaultValue: true,
      },
      {
        key: 'show_navigation',
        label: 'Show Navigation Arrows',
        type: 'boolean',
        description: 'Show left/right arrow buttons',
        defaultValue: true,
      },
    ],
  },

  // =========================================================================
  // UTILITY SECTIONS
  // =========================================================================
  rich_content: {
    type: 'rich_content',
    name: 'Rich Text Content',
    description: 'Freeform HTML/Markdown content block',
    icon: 'FileText',
    category: 'utility',
    supportsMultiple: true,
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 'medium',
      paddingBottom: 'medium',
    },
    defaultContent: {
      content: '',
    },
    fields: [
      {
        key: 'content',
        label: 'Content',
        type: 'richtext',
        description: 'Enter your content using the rich text editor',
      },
    ],
  },

  spacer: {
    type: 'spacer',
    name: 'Spacer',
    description: 'Empty vertical space',
    icon: 'SeparatorHorizontal',
    category: 'utility',
    supportsMultiple: true,
    defaultSettings: {
      paddingTop: 'large',
      paddingBottom: 'none',
    },
    defaultContent: {},
    fields: [],
  },

  divider: {
    type: 'divider',
    name: 'Divider',
    description: 'Horizontal line separator',
    icon: 'Minus',
    category: 'utility',
    supportsMultiple: true,
    defaultSettings: {
      paddingTop: 'medium',
      paddingBottom: 'medium',
    },
    defaultContent: {
      style: 'solid', // 'solid' | 'dashed' | 'dotted'
    },
    fields: [
      {
        key: 'style',
        label: 'Line Style',
        type: 'select',
        options: [
          { value: 'solid', label: 'Solid' },
          { value: 'dashed', label: 'Dashed' },
          { value: 'dotted', label: 'Dotted' },
        ],
        defaultValue: 'solid',
      },
    ],
  },
}

// =============================================================================
// Default Sections for Page Types
// =============================================================================

export const DEFAULT_PAGE_SECTIONS: Record<string, string[]> = {
  home: [
    'hero_booking',
    'fleet_carousel',
    'intercity_routes',
    'features_grid',
    'cta_banner',
    'reviews_carousel',
    'services_grid',
  ],
  about: [
    'hero_simple',
    'story_section',
    'features_grid',
    'stats_section',
    'cta_banner',
  ],
  contact: ['hero_simple', 'contact_form', 'cta_banner'],
  fleet: ['hero_simple', 'fleet_grid', 'cta_banner'],
  faq: ['hero_simple', 'faq_accordion', 'cta_banner'],
  custom: ['hero_simple', 'rich_content'],
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get a section definition by type
 */
export function getSectionDefinition(
  type: string
): SectionTypeDefinition | undefined {
  return SECTION_REGISTRY[type]
}

/**
 * Get all sections filtered by category
 */
export function getSectionsByCategory(
  category?: SectionCategory
): SectionTypeDefinition[] {
  const sections = Object.values(SECTION_REGISTRY)
  if (!category) return sections
  return sections.filter((s) => s.category === category)
}

/**
 * Get default section types for a page type
 */
export function getDefaultSectionsForPageType(
  pageType: string
): string[] {
  return DEFAULT_PAGE_SECTIONS[pageType] || DEFAULT_PAGE_SECTIONS.custom
}

/**
 * Get all available section types as an array
 */
export function getAllSectionTypes(): SectionTypeDefinition[] {
  return Object.values(SECTION_REGISTRY)
}

/**
 * Get all category definitions for UI display
 */
export function getSectionCategories(): Array<{
  id: SectionCategory
  name: string
  icon: string
}> {
  return [
    { id: 'hero', name: 'Headers', icon: 'Image' },
    { id: 'content', name: 'Content', icon: 'FileText' },
    { id: 'features', name: 'Features', icon: 'Grid3x3' },
    { id: 'cta', name: 'Call to Action', icon: 'Megaphone' },
    { id: 'social', name: 'Social Proof', icon: 'Star' },
    { id: 'utility', name: 'Utility', icon: 'Wrench' },
  ]
}
